import { useState, useEffect } from 'react';
import {
  Sparkles,
  Coins,
  TrendingUp,
  Settings,
  History,
  Zap,
  Loader2,
  AlertCircle,
  Check,
  CreditCard,
  BarChart3,
} from 'lucide-react';
import { aiApi } from '../lib/api';
import { EmptyState } from '../components/EmptyState';

interface TokenBalance {
  id: string;
  balance: number;
  isUnlimited: boolean;
}

interface TokenTransaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  createdAt: string;
}

interface UsageData {
  last30Days: { totalTokens: number; requestCount: number };
  recentLogs: Array<{
    id: string;
    provider: string;
    model: string;
    action: string;
    totalTokens: number;
    status: string;
    createdAt: string;
  }>;
}

interface AiSettings {
  preferredProvider: string;
  autoReply: boolean;
}

interface AiProvider {
  id: string;
  name: string;
  displayName: string;
  modelId: string;
  isActive: boolean;
}

export function AiManagementPage() {
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'settings'>('overview');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, transactionsRes, usageRes, settingsRes, providersRes] = await Promise.all([
        aiApi.getBalance(),
        aiApi.getTransactions(),
        aiApi.getUsage(),
        aiApi.getSettings(),
        aiApi.getProviders(),
      ]);
      setBalance(balanceRes.data.data);
      setTransactions(transactionsRes.data.data);
      setUsage(usageRes.data.data);
      setSettings(settingsRes.data.data);
      setProviders(providersRes.data.data);
    } catch {
      showToast('Błąd ładowania danych AI', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (data: Partial<AiSettings>) => {
    try {
      await aiApi.updateSettings(data);
      setSettings(prev => prev ? { ...prev, ...data } : null);
      showToast('Ustawienia zapisane');
    } catch {
      showToast('Błąd zapisu ustawień', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  const tabs = [
    { key: 'overview' as const, label: 'Przegląd', icon: BarChart3 },
    { key: 'history' as const, label: 'Historia', icon: History },
    { key: 'settings' as const, label: 'Ustawienia', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-tertiary/20 border border-accent-primary/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-accent-primary" />
          </div>
          Sztuczna inteligencja
        </h1>
        <p className="text-sm text-text-secondary mt-1">Zarządzaj tokenami i ustawieniami AI</p>
      </div>

      {/* Balance card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-primary/10 flex items-center justify-center">
              <Coins className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Saldo tokenów</p>
              <p className="text-2xl font-bold text-text-primary">
                {balance?.isUnlimited ? (
                  <span className="text-accent-primary flex items-center gap-1">
                    <Zap className="w-5 h-5" /> Nieograniczone
                  </span>
                ) : (
                  (balance?.balance || 0).toLocaleString('pl-PL')
                )}
              </p>
            </div>
          </div>
          {!balance?.isUnlimited && (
            <button className="btn-primary w-full text-sm flex items-center justify-center gap-2 mt-2">
              <CreditCard className="w-4 h-4" /> Doładuj tokeny
            </button>
          )}
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-tertiary/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-accent-tertiary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Użyto (30 dni)</p>
              <p className="text-2xl font-bold text-text-primary">
                {(usage?.last30Days.totalTokens || 0).toLocaleString('pl-PL')}
              </p>
            </div>
          </div>
          <p className="text-xs text-text-muted">
            {usage?.last30Days.requestCount || 0} zapytań
          </p>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Preferowany model</p>
              <p className="text-lg font-bold text-text-primary capitalize">
                {settings?.preferredProvider || 'Claude'}
              </p>
            </div>
          </div>
          <p className="text-xs text-text-muted">
            {providers.find(p => p.name === settings?.preferredProvider)?.modelId || ''}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-surface-raised/50 p-1 rounded-xl border border-border-subtle w-fit animate-slide-up" style={{ animationDelay: '0.05s' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Ostatnie aktywności</h2>
          {usage?.recentLogs && usage.recentLogs.length > 0 ? (
            <div className="space-y-2">
              {usage.recentLogs.map(log => (
                <div key={log.id} className="card p-4 flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    log.status === 'success' ? 'bg-success/10' : 'bg-danger/10'
                  }`}>
                    {log.status === 'success' ? (
                      <Check className="w-4 h-4 text-success" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-danger" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary">
                      {log.action === 'message_reply' ? 'Odpowiedź na email' : log.action === 'comment_reply' ? 'Odpowiedź na komentarz FB' : log.action === 'template_personalization' ? 'Personalizacja szablonu' : log.action}
                    </p>
                    <p className="text-xs text-text-muted">
                      {log.provider} / {log.model} — {log.totalTokens.toLocaleString('pl-PL')} tokenów
                    </p>
                  </div>
                  <span className="text-xs text-text-muted">
                    {new Date(log.createdAt).toLocaleString('pl-PL')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Sparkles}
              title="Brak aktywności AI"
              description="Użyj funkcji odpowiedzi AI w skrzynce wiadomości"
              size="sm"
            />
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Historia transakcji tokenów</h2>
          {transactions.length > 0 ? (
            <div className="space-y-2">
              {transactions.map(tx => (
                <div key={tx.id} className="card p-4 flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    tx.amount > 0 ? 'bg-success/10' : 'bg-orange-500/10'
                  }`}>
                    {tx.amount > 0 ? (
                      <TrendingUp className="w-4 h-4 text-success" />
                    ) : (
                      <TrendingUp className="w-4 h-4 text-orange-400 rotate-180" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-primary">
                      {tx.description || (tx.type === 'purchase' ? 'Zakup tokenów' : tx.type === 'admin_grant' ? 'Doładowanie od admina' : 'Użycie AI')}
                    </p>
                    <p className="text-xs text-text-muted">
                      {new Date(tx.createdAt).toLocaleString('pl-PL')}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold ${tx.amount > 0 ? 'text-success' : 'text-orange-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('pl-PL')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={History}
              title="Brak transakcji"
              size="sm"
            />
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-text-primary">Ustawienia AI</h2>

          {/* Preferred model */}
          <div className="card p-5 space-y-4">
            <h3 className="text-sm font-medium text-text-primary">Preferowany model językowy</h3>
            <p className="text-xs text-text-secondary">
              Wybierz model AI, który będzie używany do generowania odpowiedzi na wiadomości.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {providers.map(provider => (
                <button
                  key={provider.name}
                  onClick={() => updateSettings({ preferredProvider: provider.name })}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    settings?.preferredProvider === provider.name
                      ? 'border-accent-primary/50 bg-accent-primary/10'
                      : 'border-border-subtle bg-surface-raised/50 hover:border-border'
                  }`}
                >
                  <p className={`text-sm font-semibold ${
                    settings?.preferredProvider === provider.name ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    {provider.displayName}
                  </p>
                  <p className="text-xs text-text-muted mt-1">{provider.modelId}</p>
                  {settings?.preferredProvider === provider.name && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-accent-primary">
                      <Check className="w-3 h-3" /> Aktywny
                    </div>
                  )}
                </button>
              ))}

              {providers.length === 0 && (
                <div className="col-span-3">
                  <EmptyState
                    icon={AlertCircle}
                    title="Brak skonfigurowanych dostawców AI"
                    description="Poproś administratora o konfigurację."
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border shadow-lg ${
          toast.type === 'success'
            ? 'bg-success/10 border-success/30 text-success'
            : 'bg-danger/10 border-danger/30 text-danger'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
